import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../constants";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Personal Backer Referral card.
 * Shows the user's unique invite code (e.g. BACKER-AB23XK), their /invite/ share link,
 * and live referral stats (codes redeemed + lifetime conversions attributed to them).
 */
export default function MyReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/auth/my-invite`, { withCredentials: true, headers: authHeaders() })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card-glass p-5" data-testid="my-referral-card-loading">
        <p className="text-[#E8E4DC] text-sm">Loading your invite code…</p>
      </div>
    );
  }

  if (!data) return null;

  const inviteUrl = `${window.location.origin}/invite/${data.code}`;

  const safeCopy = async (value, label) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        toast.success(`${label} copied`);
        return;
      }
      throw new Error("clipboard unavailable");
    } catch {
      // Fallback: select an off-screen textarea so the user can manually copy
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        toast.success(`${label} copied`);
      } catch {
        toast.error(`Press Ctrl/Cmd+C — clipboard blocked`);
      }
      document.body.removeChild(ta);
    }
  };

  const copyLink = () => safeCopy(inviteUrl, "Invite link");
  const copyCode = () => safeCopy(data.code, "Code");

  return (
    <div className="card-glass p-5" data-testid="my-referral-card">
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-4 h-4 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path d="M17 8h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2v-9a2 2 0 012-2h2m10 0V6a2 2 0 00-2-2H9a2 2 0 00-2 2v2m10 0H7" />
        </svg>
        <h3 className="font-heading text-white font-semibold">Your Personal Invite Code</h3>
      </div>
      <p className="text-[#E8E4DC] text-xs mb-3">
        Share this code or link — friends get <span className="text-[#A3E635] font-semibold">{data.pct}% off lifetime</span>, and every redemption is attributed back to you.
      </p>

      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={data.code}
          readOnly
          onClick={(e) => e.target.select()}
          className="input-dark flex-1 min-w-0 font-mono text-sm text-center text-[#A3E635]"
          data-testid="my-referral-code"
        />
        <button onClick={copyCode} className="btn-secondary text-xs px-4 shrink-0 whitespace-nowrap" data-testid="my-referral-copy-code">
          Copy
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={inviteUrl}
          readOnly
          onClick={(e) => e.target.select()}
          className="input-dark flex-1 min-w-0 text-xs font-mono"
          data-testid="my-referral-url"
        />
        <button onClick={copyLink} className="btn-secondary text-xs px-4 shrink-0 whitespace-nowrap" data-testid="my-referral-copy-link">
          Copy Link
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-white/10">
        <div className="text-center" title="People who applied your code (intent — partial-discount or free-lifetime). Includes redemptions still pending payment.">
          <div className="text-2xl font-bold text-[#A3E635]" data-testid="my-referral-redemptions">
            {data.redemptions_count}
          </div>
          <div className="text-[#E8E4DC]/70 text-xs uppercase tracking-wide">Redemptions</div>
          <div className="text-[#E8E4DC]/40 text-[10px] mt-0.5">code applied</div>
        </div>
        <div className="text-center" title="Friends who completed a paid lifetime purchase using your personal code.">
          <div className="text-2xl font-bold text-[#A3E635]" data-testid="my-referral-conversions">
            {data.lifetime_conversions}
          </div>
          <div className="text-[#E8E4DC]/70 text-xs uppercase tracking-wide">Conversions</div>
          <div className="text-[#E8E4DC]/40 text-[10px] mt-0.5">paid lifetime</div>
        </div>
      </div>
    </div>
  );
}
